'use client';

import {
  Avatar,
  Divider,
  List,
  Menu,
  MenuItem,
  Stack,
  Tab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { drop, isEmpty, map, size, sortBy, take } from 'lodash-es';
import React, { Fragment, useEffect } from 'react';
import { MdArrowDropDown } from 'react-icons/md';
import useStore from '../../stores/store';
import { authorizedServerFetch } from '@/actions/server.actions';
import StyledBoldTypography from '../Styled/StyledBoldTypography';
import StyledTabs from '../Styled/StyledTabs';
import { LikeTypeDetails } from '../../constants/likeDetails.constant';
import PostReactionCard from './PostReactionCard';
import Dialog from '@ui/Dialog';
import Typography from '@ui/Typography';

function PostReactionDialog() {
  const { open, post, type, comment, hideDialog } = useStore(
    (state) => state.postContributorDialog,
  );
  const theme = useTheme();

  const moreButtonRef = React.useRef(null);

  const [showDialog, setShowDialog] = React.useState(false);
  const [reactions, setReactions] = React.useState({});
  const [tabs, setTabs] = React.useState([]);
  const [tabType, setTabType] = React.useState('all');
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [moreTabValue, setMoreTabValue] = React.useState('_more');
  const [loading, setLoading] = React.useState(false);
  const [noReactionsFromUserOrg, setNoReactionsFromUserOrg] =
    React.useState(false);

  const maxTabs = useMediaQuery(theme.breakpoints.down('sm'))
    ? Math.min(2, tabs.length)
    : tabs.length;

  useEffect(() => {
    setShowDialog(open && type === 'reactions');
  }, [open, type]);

  const handleClose = () => {
    setAnchorEl(null);
    setTabs([]);
    setReactions({});
    setTabType('all');
    setMoreTabValue('_more');
    setLoading(false);
    setNoReactionsFromUserOrg(false);
    hideDialog();
  };

  useEffect(() => {
    const getReactions = async () => {
      if (comment) {
        const { data } = await authorizedServerFetch(
          `/posts/${post.id}/comments/${comment.id}/reactions`,
          {
            method: 'POST',
            body: {},
          },
        );
        return data?.data;
      }

      const { data } = await authorizedServerFetch(
        `/posts/${post.id}/reactions`,
      );
      return data?.data;
    };

    const prepareReactions = async () => {
      setLoading(true);
      const data = await getReactions();
      // console.log('prepareReactions', data);

      if (data) {
        if (isEmpty(data)) {
          setNoReactionsFromUserOrg(true);
        } else {
          const reactionsHash = {
            all: [...data],
          };
          data.forEach((item) => {
            if (reactionsHash[item.likeType]) {
              reactionsHash[item.likeType].push(item);
            } else {
              reactionsHash[item.likeType] = [item];
            }
          });

          const tabsToShow = map(reactionsHash, (value, key) => {
            if (key === 'all') {
              return { label: 'All', count: size(value), type: key, order: 0 };
            }
            const index = LikeTypeDetails.findIndex((dta) => dta.type === key);
            const reactionObj = LikeTypeDetails[index];
            return {
              ...reactionObj,
              count: size(value),
              order: index,
            };
          });

          setReactions(reactionsHash);
          setTabs(sortBy(tabsToShow, 'order'));
        }
      } else {
        handleClose();
      }
      setLoading(false);
    };
    if (showDialog && post) {
      setTabType('all');
      prepareReactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post, showDialog]);

  const handleShowMore = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleTabChange = async (newValue) => {
    const isTabVisible = take(tabs, maxTabs).some(
      (tab) => tab.type === newValue,
    );
    if (isTabVisible) {
      setTabType(newValue);
    } else {
      setAnchorEl(moreButtonRef.current);
    }
  };

  if (!showDialog) return null;

  return (
    <Dialog
      title="Reactions"
      isOpen={showDialog}
      maxWidth="md"
      loading={loading}
      onClose={handleClose}>
      {noReactionsFromUserOrg && (
        <div>
          <div className="flex w-full items-center justify-center">
            <Typography variant="body1">
              Thanks for checking! Although no members of your organization have
              reacted to this post yet, it has received support from members of
              other organizations.
            </Typography>
          </div>
        </div>
      )}

      {!noReactionsFromUserOrg && (
        <div>
          <div className="flex flex-row items-center">
            <StyledTabs
              compact
              value={tabType}
              indicatorColor="secondary"
              onChange={(e, val) => handleTabChange(val)}>
              {take(tabs, maxTabs).map((tab) => {
                if (tab.type === 'all') {
                  return (
                    <Tab
                      label={
                        <StyledBoldTypography
                          variant="body2"
                          color="textSecondary">{`All ${tab.count}`}</StyledBoldTypography>
                      }
                      key={tab.type}
                      value="all"
                    />
                  );
                }
                return (
                  <Tab
                    value={tab.type}
                    label={
                      <StyledBoldTypography
                        variant="body2"
                        color="textSecondary">
                        {tab.count}
                      </StyledBoldTypography>
                    }
                    iconPosition="start"
                    key={tab.type}
                    icon={
                      <Avatar
                        src={tab.icon}
                        alt={tab.label}
                        sx={{
                          width: 24,
                          height: 24,
                          bgcolor: 'primary.lightbg',
                          padding: 0.25,
                        }}
                      />
                    }
                  />
                );
              })}
              {tabs.length > maxTabs && (
                <Tab
                  ref={moreButtonRef}
                  value={moreTabValue}
                  label={
                    <button type="button" onClick={handleShowMore}>
                      <Stack direction="row" alignItems="center">
                        <StyledBoldTypography
                          variant="body2"
                          color="textSecondary">
                          More
                        </StyledBoldTypography>
                        <MdArrowDropDown />
                      </Stack>
                    </button>
                  }
                  iconPosition="end"
                  disableRipple
                  disableFocusRipple
                />
              )}
            </StyledTabs>
          </div>
          <List sx={{ transform: 'translateX(-16px)' }}>
            {reactions[tabType] &&
              reactions[tabType].map((userData) => (
                <Fragment key={userData.id}>
                  <PostReactionCard key={userData.id} user={userData} />
                  <Divider variant="inset" component="li" />
                </Fragment>
              ))}
          </List>
          {tabs.length > maxTabs && (
            <Menu
              anchorEl={anchorEl}
              open={!!anchorEl}
              sx={{ '& .MuiMenu-paper': { width: 120 } }}
              onClose={() => setAnchorEl(null)}>
              {drop(tabs, maxTabs).map((tab) => (
                <MenuItem
                  key={tab.type}
                  onClick={() => {
                    setMoreTabValue(tab.type);
                    setTabType(tab.type);
                    setAnchorEl(null);
                  }}>
                  <Stack direction="row" alignItems="center" gap={2}>
                    <Avatar
                      src={tab.icon}
                      alt={tab.label}
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: 'primary.lightbg',
                        padding: 0.5,
                      }}
                    />
                    <StyledBoldTypography variant="body1" color="textSecondary">
                      {tab.count}
                    </StyledBoldTypography>
                  </Stack>
                </MenuItem>
              ))}
            </Menu>
          )}
        </div>
      )}
    </Dialog>
  );
}

export default PostReactionDialog;
